/**
 * Minimal SVG builder with deterministic output.
 *
 * Deterministic matters: generated art is gitignored and rebuilt, so two
 * developers must get byte-identical files or the build looks dirty. Every
 * numeric value is rounded to a fixed precision for that reason.
 */

const PRECISION = 3;

export function n(value: number): string {
  const r = Number(value.toFixed(PRECISION));
  // Avoid "-0", which differs textually but not visually.
  return String(r === 0 ? 0 : r);
}

export type Attrs = Record<string, string | number | undefined>;

function renderAttrs(attrs: Attrs): string {
  return Object.entries(attrs)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}="${typeof v === 'number' ? n(v) : escapeAttr(String(v))}"`)
    .join(' ');
}

export function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function tag(name: string, attrs: Attrs = {}, children?: string): string {
  const a = renderAttrs(attrs);
  const open = a ? `${name} ${a}` : name;
  return children === undefined || children === ''
    ? `<${open}/>`
    : `<${open}>${children}</${name}>`;
}

export function svg(width: number, height: number, body: string, extra: Attrs = {}): string {
  return tag(
    'svg',
    {
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: `0 0 ${n(width)} ${n(height)}`,
      width,
      height,
      ...extra,
    },
    body,
  );
}

export const group = (attrs: Attrs, children: string): string => tag('g', attrs, children);
export const rect = (attrs: Attrs): string => tag('rect', attrs);
export const circle = (attrs: Attrs): string => tag('circle', attrs);
export const ellipse = (attrs: Attrs): string => tag('ellipse', attrs);
export const path = (d: string, attrs: Attrs = {}): string => tag('path', { d, ...attrs });
export const line = (x1: number, y1: number, x2: number, y2: number, attrs: Attrs = {}): string =>
  tag('line', { x1, y1, x2, y2, ...attrs });
export const polygon = (points: readonly [number, number][], attrs: Attrs = {}): string =>
  tag('polygon', { points: points.map(([x, y]) => `${n(x)},${n(y)}`).join(' '), ...attrs });
export const text = (content: string, attrs: Attrs = {}): string =>
  tag('text', attrs, escapeText(content));
export const defs = (children: string): string => tag('defs', {}, children);

/**
 * Deterministic jitter. Used for texture grain and crack paths so that assets
 * look hand-made rather than mechanically perfect, while remaining reproducible.
 */
export class ArtRng {
  private s: number;
  constructor(seed: number | string) {
    this.s = (typeof seed === 'number' ? seed : hashString(seed)) >>> 0 || 1;
  }
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo);
  }
  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)] as T;
  }
}

export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Rounded-rect path, used where `rect` cannot express per-corner radii. */
export function roundedRectPath(
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): string {
  const rr = Math.min(r, w / 2, h / 2);
  return [
    `M${n(x + rr)} ${n(y)}`,
    `H${n(x + w - rr)}`,
    `A${n(rr)} ${n(rr)} 0 0 1 ${n(x + w)} ${n(y + rr)}`,
    `V${n(y + h - rr)}`,
    `A${n(rr)} ${n(rr)} 0 0 1 ${n(x + w - rr)} ${n(y + h)}`,
    `H${n(x + rr)}`,
    `A${n(rr)} ${n(rr)} 0 0 1 ${n(x)} ${n(y + h - rr)}`,
    `V${n(y + rr)}`,
    `A${n(rr)} ${n(rr)} 0 0 1 ${n(x + rr)} ${n(y)}`,
    'Z',
  ].join(' ');
}

/** A jagged polyline, for cracks and torn edges. */
export function crackPath(
  rng: ArtRng,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  segments: number,
  jitter: number,
): string {
  const pts: string[] = [`M${n(x1)} ${n(y1)}`];
  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const jx = i === segments ? 0 : rng.range(-jitter, jitter);
    const jy = i === segments ? 0 : rng.range(-jitter, jitter);
    pts.push(`L${n(x1 + (x2 - x1) * t + jx)} ${n(y1 + (y2 - y1) * t + jy)}`);
  }
  return pts.join(' ');
}

// ---------------------------------------------------------------------------
// Print techniques (Style Bible §2)
// ---------------------------------------------------------------------------

/**
 * A halftone screen as an SVG pattern.
 *
 * Shading in this system is dot SIZE, never a gradient or a mixed colour — that
 * is what separates a printed look from a flat-design pastiche. The grid is
 * rotated to the classic 45° screen angle.
 */
export function halftonePattern(
  id: string,
  color: string,
  dot: number,
  grid: number,
  angle = 45,
): string {
  return tag(
    'pattern',
    {
      id,
      width: grid,
      height: grid,
      patternUnits: 'userSpaceOnUse',
      patternTransform: `rotate(${n(angle)})`,
    },
    circle({ cx: grid / 2, cy: grid / 2, r: dot / 2, fill: color }),
  );
}

/**
 * Paper grain: sparse specks at low opacity. Keeps a large flat area of ink from
 * reading as a screen fill.
 */
export function paperGrain(id: string, color: string, seed: string): string {
  const rng = new ArtRng(`grain:${seed}`);
  const specks = Array.from({ length: 26 }, () =>
    circle({
      cx: rng.range(0, 64),
      cy: rng.range(0, 64),
      r: rng.range(0.4, 1.3),
      fill: color,
      opacity: rng.range(0.03, 0.06),
    }),
  ).join('');
  return tag('pattern', { id, width: 64, height: 64, patternUnits: 'userSpaceOnUse' }, specks);
}

/**
 * Colour-plate offset — the single detail that dates the look.
 *
 * Cheap offset printing did not register its plates perfectly, so the colour sat
 * a hair off the black key line. Direction is derived from the seed so assets do
 * not all lean the same way, which would read as a deliberate drop shadow.
 */
export function misregisterOffset(seed: string, canvas: number, amount: number): [number, number] {
  const rng = new ArtRng(`misreg:${seed}`);
  const angle = rng.range(0, Math.PI * 2);
  const d = canvas * amount;
  return [Math.cos(angle) * d, Math.sin(angle) * d];
}

/** Wrap colour artwork so it prints slightly off the key line. */
export function misregistered(seed: string, canvas: number, amount: number, body: string): string {
  const [dx, dy] = misregisterOffset(seed, canvas, amount);
  return group({ transform: `translate(${n(dx)} ${n(dy)})` }, body);
}

/**
 * The fold down the middle of a board. Two plates of card, hinged with tape —
 * every board game of the era had this crease and it never flattened out again.
 */
export function boardFold(width: number, height: number, shadowColor: string): string {
  const x = width / 2;
  return (
    tag('rect', { x: x - width * 0.035, y: 0, width: width * 0.07, height, fill: shadowColor, opacity: 0.16 }) +
    path(`M${n(x)} 0 V${n(height)}`, { stroke: shadowColor, 'stroke-width': 2, opacity: 0.55 })
  );
}

/**
 * Yellowed edges. Old cardstock darkens from the outside in, where it was
 * handled and where light reached it.
 */
export function agedEdges(width: number, height: number, agedColor: string): string {
  // Concentric inset strokes, each fainter than the last: darkest at the trimmed
  // edge, fading inward. A single filled rect would flood the whole board.
  const band = Math.min(width, height) * 0.03;
  return Array.from({ length: 3 }, (_, i) =>
    tag('rect', {
      x: band * i * 0.5,
      y: band * i * 0.5,
      width: width - band * i,
      height: height - band * i,
      fill: 'none',
      stroke: agedColor,
      'stroke-width': band,
      opacity: 0.3 - i * 0.09,
    }),
  ).join('');
}
